/**
 * Design Library — hero patterns (the focal opening of a design).
 * Pure design knowledge: no pixels, geometry, or editor concepts.
 */

import { definePattern } from './_define';

export const HERO_PATTERNS = [
  definePattern({
    id: 'split-hero', name: 'Split Hero', family: 'hero',
    purpose: 'A hero divided into a copy column and an image column.',
    communicationGoal: 'Pair a decisive message with the proof of an image.',
    visualGoal: 'Two-column focal area with deliberate asymmetric balance.',
    contentGoal: 'Headline, supporting line and CTA beside a feature image.',
    marketingGoal: 'Land the promise and the primary action in one glance.',
    idealDocuments: ['Flyer', 'Poster', 'Presentation', 'Brochure', 'Facebook Post'],
    idealIndustries: ['real-estate', 'corporate', 'technology', 'travel', 'medical'],
    idealAudiences: ['professionals', 'general-public', 'families'],
    idealGenomes: ['editorial', 'corporate', 'modern', 'premium-medical'],
    compatiblePatterns: ['feature-row', 'stat-row', 'premium-cta', 'logo-strip'],
    incompatiblePatterns: ['centered-poster'],
    compositionPhilosophy: 'Asymmetric two-column balance; copy and image hold equal weight.',
    hierarchyPhilosophy: 'Headline dominates; the CTA closes the copy column.',
    typicalSections: ['headline', 'subheadline', 'cta', 'image'],
    preferredComponents: ['hero', 'cta'],
    premiumCues: ['refined-type', 'generous-whitespace'],
    thingsToAvoid: ['clutter', 'competing-focal-points'],
    variationRules: ['image-left-or-right', 'copy-emphasis', 'overlap-headline'],
    capabilities: ['image', 'cta', 'multi-column', 'image-led', 'text-led'],
    tags: ['split', 'asymmetric', 'hero'],
    expansionRules: { emphasis: 'headline', columns: 'two', imageRole: 'feature' },
  }),
  definePattern({
    id: 'split-hero-image-left', name: 'Split Hero — Image Left', family: 'hero',
    variantOf: 'split-hero',
    purpose: 'Split hero with the image leading on the left.',
    communicationGoal: 'Let the image hook the eye before the message.',
    visualGoal: 'Image-first two-column balance.',
    contentGoal: 'Feature image beside headline, supporting line and CTA.',
    marketingGoal: 'Sell with the visual, then convert with the copy.',
    idealDocuments: ['Flyer', 'Brochure', 'Presentation'],
    idealIndustries: ['real-estate', 'travel', 'restaurant'],
    idealAudiences: ['general-public', 'tourists'],
    idealGenomes: ['editorial', 'modern', 'organic'],
    compatiblePatterns: ['feature-row', 'premium-cta'],
    compositionPhilosophy: 'Image anchors the left; copy resolves on the right.',
    hierarchyPhilosophy: 'Image first, then headline, then CTA.',
    typicalSections: ['image', 'headline', 'subheadline', 'cta'],
    preferredComponents: ['hero', 'cta'],
    premiumCues: ['generous-whitespace'],
    thingsToAvoid: ['clutter'],
    variationRules: ['image-bleed', 'copy-emphasis'],
    capabilities: ['image', 'cta', 'multi-column', 'image-led'],
    tags: ['split', 'image-left', 'hero'],
    expansionRules: { emphasis: 'image', columns: 'two', imageRole: 'lead' },
  }),
  definePattern({
    id: 'split-hero-image-right', name: 'Split Hero — Image Right', family: 'hero',
    variantOf: 'split-hero',
    purpose: 'Split hero with the message leading on the left.',
    communicationGoal: 'Lead with the words, support with the image.',
    visualGoal: 'Type-first two-column balance.',
    contentGoal: 'Headline, supporting line and CTA beside a feature image.',
    marketingGoal: 'State the promise, then evidence it.',
    idealDocuments: ['Flyer', 'Presentation', 'Facebook Post'],
    idealIndustries: ['corporate', 'technology', 'medical', 'education'],
    idealAudiences: ['professionals', 'students'],
    idealGenomes: ['corporate', 'swiss', 'premium-medical'],
    compatiblePatterns: ['stat-row', 'feature-row', 'premium-cta'],
    compositionPhilosophy: 'Copy anchors the left; image resolves on the right.',
    hierarchyPhilosophy: 'Headline first, CTA beneath, image supports.',
    typicalSections: ['headline', 'subheadline', 'cta', 'image'],
    preferredComponents: ['hero', 'cta'],
    premiumCues: ['refined-type'],
    thingsToAvoid: ['low-contrast'],
    variationRules: ['copy-emphasis', 'image-bleed'],
    capabilities: ['image', 'cta', 'multi-column', 'text-led'],
    tags: ['split', 'image-right', 'hero'],
    expansionRules: { emphasis: 'headline', columns: 'two', imageRole: 'support' },
  }),
  definePattern({
    id: 'gradient-hero', name: 'Gradient Hero', family: 'hero',
    purpose: 'A hero carried by a gradient or mesh field rather than photography.',
    communicationGoal: 'Create mood and modernity without an image.',
    visualGoal: 'Luminous depth behind confident type.',
    contentGoal: 'Oversized headline with a supporting line and CTA.',
    marketingGoal: 'Feel current and premium; push the action.',
    idealDocuments: ['Instagram Post', 'Instagram Story', 'Presentation', 'Banner', 'Email Header'],
    idealIndustries: ['technology', 'travel', 'events', 'finance'],
    idealAudiences: ['professionals', 'students', 'general-public'],
    idealGenomes: ['gradient-mesh', 'glassmorphism', 'future-ui'],
    compatiblePatterns: ['feature-row', 'stat-row', 'premium-cta'],
    incompatiblePatterns: ['magazine-spread'],
    compositionPhilosophy: 'Type floats on a luminous field; depth comes from light, not objects.',
    hierarchyPhilosophy: 'Headline is the hero; the CTA glows against the field.',
    typicalSections: ['headline', 'subheadline', 'cta'],
    preferredComponents: ['hero', 'cta'],
    premiumCues: ['subtle-depth', 'considered-color'],
    thingsToAvoid: ['heavy-gradient-on-text', 'low-contrast'],
    variationRules: ['gradient-angle', 'mesh-complexity', 'type-scale'],
    capabilities: ['cta', 'text-led', 'fullbleed'],
    tags: ['gradient', 'depth', 'hero'],
    expansionRules: { emphasis: 'headline', background: 'gradient-field' },
  }),
  definePattern({
    id: 'flyer-hero', name: 'Flyer Hero', family: 'hero',
    purpose: 'A promotional hero built for a print flyer.',
    communicationGoal: 'Stop a passer-by and deliver the offer instantly.',
    visualGoal: 'Dominant hero with an unmissable offer.',
    contentGoal: 'Hero image or field, headline, offer and CTA.',
    marketingGoal: 'Convert attention into a visit or an order.',
    idealDocuments: ['Flyer', 'Pamphlet', 'Poster'],
    idealIndustries: ['restaurant', 'retail', 'fitness', 'salon', 'food'],
    idealAudiences: ['general-public', 'families'],
    idealGenomes: ['bold', 'organic', 'playful', 'retro'],
    compatiblePatterns: ['offer-section', 'discount-block', 'premium-cta', 'contact-section'],
    compositionPhilosophy: 'One dominant focal band above a decisive offer.',
    hierarchyPhilosophy: 'Offer and headline outrank everything else.',
    typicalSections: ['image', 'headline', 'offer', 'cta', 'contact'],
    preferredComponents: ['hero', 'cta', 'contact'],
    premiumCues: ['confident-scale'],
    thingsToAvoid: ['tiny-cta', 'clutter'],
    variationRules: ['offer-placement', 'hero-ratio'],
    capabilities: ['image', 'offer', 'cta', 'contact', 'image-led'],
    tags: ['flyer', 'promotional', 'hero'],
    expansionRules: { emphasis: 'offer', imageRole: 'backdrop' },
  }),
  definePattern({
    id: 'poster-hero', name: 'Poster Hero', family: 'hero',
    purpose: 'A large-format hero read from a distance.',
    communicationGoal: 'Communicate one idea at ten paces.',
    visualGoal: 'Monumental type or image with vast breathing room.',
    contentGoal: 'A single headline, a date or detail line, and a footer.',
    marketingGoal: 'Announce; drive recall rather than clicks.',
    idealDocuments: ['Poster', 'Banner'],
    idealIndustries: ['events', 'festival', 'music', 'fitness'],
    idealAudiences: ['general-public', 'students'],
    idealGenomes: ['bold', 'neo-brutalism', 'memphis', 'luxury-fashion'],
    compatiblePatterns: ['logo-strip', 'premium-cta'],
    incompatiblePatterns: ['bento-grid'],
    compositionPhilosophy: 'One monumental focal point; everything else recedes.',
    hierarchyPhilosophy: 'A single dominant element carries the whole design.',
    typicalSections: ['headline', 'subheadline', 'footer'],
    preferredComponents: ['hero', 'footer'],
    premiumCues: ['confident-scale', 'generous-whitespace'],
    thingsToAvoid: ['clutter', 'small-type'],
    variationRules: ['type-scale', 'image-or-type-led'],
    capabilities: ['image', 'text-led', 'fullbleed'],
    tags: ['poster', 'monumental', 'hero'],
    expansionRules: { emphasis: 'headline', scale: 'monumental' },
  }),
  definePattern({
    id: 'instagram-story-hero', name: 'Instagram Story Hero', family: 'hero',
    purpose: 'A vertical, thumb-stopping hero for stories.',
    communicationGoal: 'Win a two-second glance on a phone.',
    visualGoal: 'Full-bleed vertical focal image with punchy type.',
    contentGoal: 'Hero image, short headline, single CTA.',
    marketingGoal: 'Drive a swipe or a tap.',
    idealDocuments: ['Instagram Story'],
    idealIndustries: ['fashion', 'beauty', 'fitness', 'travel', 'events', 'retail'],
    idealAudiences: ['students', 'women', 'general-public'],
    idealGenomes: ['bold', 'gradient-mesh', 'playful', 'luxury-fashion'],
    compatiblePatterns: ['discount-block', 'premium-cta'],
    incompatiblePatterns: ['editorial-columns', 'magazine-spread'],
    compositionPhilosophy: 'Vertical full-bleed with safe zones top and bottom.',
    hierarchyPhilosophy: 'Image, then a very short headline, then the action.',
    typicalSections: ['image', 'headline', 'cta'],
    preferredComponents: ['hero', 'cta'],
    premiumCues: ['confident-scale'],
    thingsToAvoid: ['small-type', 'clutter'],
    variationRules: ['overlay-strength', 'type-placement'],
    capabilities: ['image', 'cta', 'fullbleed', 'image-led'],
    tags: ['story', 'vertical', 'social', 'hero'],
    expansionRules: { emphasis: 'image', orientation: 'vertical' },
  }),
  definePattern({
    id: 'youtube-thumbnail-hero', name: 'YouTube Thumbnail Hero', family: 'hero',
    purpose: 'A high-contrast thumbnail hero built for tiny sizes.',
    communicationGoal: 'Be legible and intriguing at thumbnail scale.',
    visualGoal: 'Extreme contrast, a face or subject, three or four words.',
    contentGoal: 'Subject image plus a very short punch line.',
    marketingGoal: 'Win the click against a wall of competitors.',
    idealDocuments: ['YouTube Thumbnail'],
    idealIndustries: ['entertainment', 'education', 'fitness', 'technology'],
    idealAudiences: ['general-public', 'students'],
    idealGenomes: ['bold', 'neo-brutalism'],
    compatiblePatterns: [],
    incompatiblePatterns: ['editorial-columns', 'minimal-stack'],
    compositionPhilosophy: 'Subject on one side, three or four huge words on the other.',
    hierarchyPhilosophy: 'Contrast is the hierarchy; nothing subtle survives.',
    typicalSections: ['image', 'headline'],
    preferredComponents: ['hero'],
    premiumCues: ['confident-scale'],
    thingsToAvoid: ['small-type', 'low-contrast', 'clutter'],
    variationRules: ['subject-side', 'word-count'],
    capabilities: ['image', 'text-led', 'image-led'],
    tags: ['thumbnail', 'high-contrast', 'hero'],
    expansionRules: { emphasis: 'contrast', wordBudget: 'minimal' },
  }),
];
